'use client';

import React from 'react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------

export interface SummaryPill {
    label: string;
    color: string;
    icon?: string;
    href?: string;
}

interface SummaryPillsProps {
    pills: SummaryPill[];
}

export default function SummaryPills({ pills }: SummaryPillsProps) {
    return (
        <Stack
            direction="row"
            spacing={1.5}
            sx={{
                flexWrap: 'wrap',
                gap: 1.5,
                rowGap: 1.5,
            }}
        >
            {pills.map((pill, idx) => {
                const content = (
                    <Box
                        sx={(theme) => ({
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.75,
                            py: 0.85,
                            borderRadius: 999,
                            bgcolor: alpha(pill.color, 0.1),
                            border: `1px solid ${alpha(pill.color, 0.25)}`,
                            color: pill.color,
                            transition: theme.transitions.create(['background-color', 'transform']),
                            cursor: pill.href ? 'pointer' : 'default',
                            '&:hover': pill.href
                                ? {
                                      bgcolor: alpha(pill.color, 0.18),
                                      transform: 'translateY(-1px)',
                                  }
                                : undefined,
                        })}
                    >
                        {pill.icon && <Iconify icon={pill.icon} width={16} />}
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 700,
                                fontSize: '0.78rem',
                                letterSpacing: '0.01em',
                                color: 'inherit',
                            }}
                        >
                            {pill.label}
                        </Typography>
                    </Box>
                );

                return pill.href ? (
                    <Link key={idx} href={pill.href} underline="none">
                        {content}
                    </Link>
                ) : (
                    <Box key={idx}>{content}</Box>
                );
            })}
        </Stack>
    );
}
